import { supabase } from "../supabaseClient.js";

// Consultas de Colonia. Archivo aparte de api.js a propósito: lo de
// Cumpleaños no se toca (§1 bis). Todas las tablas colonia_* tienen RLS con
// puede_colonia(): admin y padres ven y editan; empleado no recibe nada.
//
// La plata de colonia vive acá y sólo acá. Ninguna función de este archivo
// escribe en events, event_finance ni movements.

async function run(query) {
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/** Trae todas las filas de a páginas de 500 (Supabase corta en 1000). */
async function fetchAll(build, pageSize = 500) {
  const rows = [];
  for (let from = 0; ; from += pageSize) {
    const page = await run(build().range(from, from + pageSize - 1));
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
}

// ---------- Interruptor ----------

/**
 * Interruptores: colonia_empresa (tanda 1) y colonia_tanda2 (Resumen, Equipo,
 * Ingresos y egresos, Empresa y los bloques del Panel y Alertas).
 * Si la tabla no existe o no hay permiso, todo apagado: la app anda como antes.
 */
export async function fetchFlags() {
  const { data, error } = await supabase.from("app_settings").select("key, enabled");
  if (error || !data) return { colonia: false, tanda2: false };
  const on = (k) => data.some((r) => r.key === k && r.enabled === true);
  // La tanda 2 se apoya en la 1: sin Colonia nueva no se muestra.
  return { colonia: on("colonia_empresa"), tanda2: on("colonia_empresa") && on("colonia_tanda2") };
}

/** Estado del interruptor de la Colonia nueva (tanda 1). Si falla, apagado. */
export async function fetchColoniaFlag() {
  return (await fetchFlags()).colonia;
}

export async function setColoniaFlag(enabled, key = "colonia_empresa") {
  await run(supabase.from("app_settings").update({ enabled }).eq("key", key));
}

// ---------- Listas editables ----------

const LISTAS = {
  turnos: "colonia_turnos",
  plazos: "colonia_plazos",
  canales: "colonia_canales",
  cuentas: "colonia_cuentas",
};

export async function fetchListas() {
  const [turnos, plazos, canales, cuentas, colegios] = await Promise.all([
    run(supabase.from(LISTAS.turnos).select("*").order("orden")),
    run(supabase.from(LISTAS.plazos).select("*").order("orden")),
    run(supabase.from(LISTAS.canales).select("*").order("orden")),
    run(supabase.from(LISTAS.cuentas).select("*").order("orden")),
    run(supabase.from("colonia_colegios").select("*").order("nombre")),
  ]);
  return { turnos, plazos, canales, cuentas, colegios };
}

export async function saveListaItem(lista, row, id = null) {
  const table = LISTAS[lista];
  if (id) return run(supabase.from(table).update(row).eq("id", id).select().single());
  return run(supabase.from(table).insert(row).select().single());
}

/** Borra un elemento de una lista. La base lo impide si ya está en uso. */
export async function deleteListaItem(lista, id) {
  await run(supabase.from(LISTAS[lista]).delete().eq("id", id));
}

export async function saveColegio(nombre) {
  return run(supabase.from("colonia_colegios").insert({ nombre, nombre_norm: nombre }).select().single());
}

export async function renameColegio(id, nombre) {
  await run(supabase.from("colonia_colegios").update({ nombre }).eq("id", id));
}

export async function unirColegios(quedaId, saleId) {
  await run(supabase.rpc("colonia_unir_colegios", { p_queda: quedaId, p_sale: saleId }));
}

// ---------- Temporada ----------

export async function fetchColoniaSeasons() {
  return run(supabase.from("seasons").select("*").order("season_start", { ascending: false }));
}

export async function updateSeasonConfig(id, changes) {
  await run(supabase.from("seasons").update(changes).eq("id", id));
}

// ---------- Tarifario ----------

export async function fetchTarifas(seasonId) {
  return run(supabase.from("colonia_tarifas").select("*").eq("season_id", seasonId));
}

/** Guarda una celda del tarifario (crea o actualiza por su clave natural). */
export async function upsertTarifa(row) {
  return run(
    supabase
      .from("colonia_tarifas")
      .upsert(row, { onConflict: "season_id,mes,condicion,turno_id,plazo_id" })
      .select()
      .single()
  );
}

export async function upsertTarifas(rows) {
  if (rows.length === 0) return [];
  return run(
    supabase
      .from("colonia_tarifas")
      .upsert(rows, { onConflict: "season_id,mes,condicion,turno_id,plazo_id" })
      .select()
  );
}

export async function fetchBeneficios(seasonId) {
  return run(supabase.from("colonia_beneficios").select("*").eq("season_id", seasonId));
}

export async function upsertBeneficio(row) {
  return run(
    supabase
      .from("colonia_beneficios")
      .upsert(row, { onConflict: "season_id,mes,condicion,turno_id" })
      .select()
      .single()
  );
}

export async function deleteBeneficio(id) {
  await run(supabase.from("colonia_beneficios").delete().eq("id", id));
}

// ---------- Inscripciones ----------

const INSCRIPCION_SELECT = `
  id, season_id, child_id, estado, baja_motivo, baja_at, enrolled_at, notes,
  amount_due, amount_paid, child_name, guardian_name, contact, created_at,
  chico:colonia_chicos (
    id, nombre, apellido, edad, condicion, colegio_id, canal_id, referido_por,
    grado, observaciones, combi, direccion, responsable_id,
    responsable:colonia_responsables ( id, nombre, whatsapp, whatsapp_norm, email )
  ),
  items:colonia_items ( * )
`;

/** Inscripciones nuevas (con chico) de una temporada, con ítems, paginadas. */
export async function fetchInscripciones(seasonId) {
  return fetchAll(() =>
    supabase
      .from("enrollments")
      .select(INSCRIPCION_SELECT)
      .eq("season_id", seasonId)
      .not("child_id", "is", null)
      .order("created_at", { ascending: true })
  );
}

/**
 * Inscripciones cargadas con la pantalla anterior de Colonia: sin ficha de
 * chico ni modalidad. Se muestran aparte para completarlas (0023).
 */
export async function fetchInscripcionesLegado(seasonId) {
  return fetchAll(() =>
    supabase
      .from("enrollments")
      .select("id, season_id, child_name, guardian_name, contact, amount_due, amount_paid, paid_at, enrolled_at, notes, created_at")
      .eq("season_id", seasonId)
      .is("child_id", null)
      .order("child_name")
  );
}

export async function fetchCuotasEstado(seasonId) {
  return fetchAll(() =>
    supabase.from("colonia_cuotas_estado").select("*").eq("season_id", seasonId).order("vence")
  );
}

export async function fetchInscripcion(id) {
  return run(supabase.from("enrollments").select(INSCRIPCION_SELECT).eq("id", id).single());
}

export async function fetchCuotasDeInscripcion(enrollmentId) {
  return run(
    supabase.from("colonia_cuotas_estado").select("*").eq("enrollment_id", enrollmentId).order("vence").order("numero")
  );
}

export async function fetchCobrosDeInscripcion(enrollmentId) {
  return run(
    supabase
      .from("colonia_cobros")
      .select("*, cuota:colonia_cuotas!inner(enrollment_id, item_id, numero)")
      .eq("cuota.enrollment_id", enrollmentId)
      .order("fecha", { ascending: false })
      .order("created_at", { ascending: false })
  );
}

export async function fetchHistorialDeInscripcion(enrollmentId, ids) {
  // Historial propio del chico (decisión 3 del mapa de riesgo): todo lo que
  // tocó a la inscripción, su chico, sus ítems, cuotas y cobros.
  const rowIds = [enrollmentId, ...ids].filter(Boolean);
  return run(
    supabase
      .from("audit_log")
      .select("id, table_name, row_id, action, actor_name, at, before, after")
      .in("row_id", rowIds)
      .order("at", { ascending: false })
      .limit(100)
  );
}

/** Responsables con ese WhatsApp (para detectar hermanos). */
export async function buscarResponsable(whatsappNorm) {
  if (!whatsappNorm) return [];
  return run(
    supabase
      .from("colonia_responsables")
      .select("id, nombre, whatsapp, email, chicos:colonia_chicos(id, nombre, apellido)")
      .eq("whatsapp_norm", whatsappNorm)
  );
}

export async function saveResponsable(row, id = null) {
  if (id) return run(supabase.from("colonia_responsables").update(row).eq("id", id).select().single());
  return run(supabase.from("colonia_responsables").insert(row).select().single());
}

export async function saveChico(row, id = null) {
  if (id) return run(supabase.from("colonia_chicos").update(row).eq("id", id).select().single());
  return run(supabase.from("colonia_chicos").insert(row).select().single());
}

export async function saveEnrollmentRow(row, id = null) {
  if (id) return run(supabase.from("enrollments").update(row).eq("id", id).select().single());
  return run(supabase.from("enrollments").insert(row).select().single());
}

export async function insertItem(row) {
  return run(supabase.from("colonia_items").insert(row).select().single());
}

export async function updateItem(id, row) {
  return run(supabase.from("colonia_items").update(row).eq("id", id).select().single());
}

export async function deleteItem(id) {
  await run(supabase.from("colonia_items").delete().eq("id", id));
}

export async function insertCuotas(rows) {
  if (rows.length === 0) return [];
  return run(supabase.from("colonia_cuotas").insert(rows).select());
}

export async function updateCuota(id, row) {
  await run(supabase.from("colonia_cuotas").update(row).eq("id", id));
}

export async function deleteCuota(id) {
  await run(supabase.from("colonia_cuotas").delete().eq("id", id));
}

// ---------- Cobros ----------

export async function insertCobro(row) {
  return run(supabase.from("colonia_cobros").insert(row).select().single());
}

export async function anularCobro(id, motivo) {
  await run(
    supabase
      .from("colonia_cobros")
      .update({ anulado: true, motivo_anulacion: motivo, anulado_at: new Date().toISOString() })
      .eq("id", id)
  );
}

/** Para el Backup completo: todo lo de Colonia nueva, plano. */
export async function fetchColoniaBackup() {
  const [inscripciones, cuotas, cobros, tarifas, beneficios, listas] = await Promise.all([
    fetchAll(() => supabase.from("enrollments").select(INSCRIPCION_SELECT).not("child_id", "is", null)),
    fetchAll(() => supabase.from("colonia_cuotas_estado").select("*")),
    fetchAll(() => supabase.from("colonia_cobros").select("*")),
    fetchAll(() => supabase.from("colonia_tarifas").select("*")),
    fetchAll(() => supabase.from("colonia_beneficios").select("*")),
    fetchListas(),
  ]);
  return { inscripciones, cuotas, cobros, tarifas, beneficios, listas };
}

/**
 * Crea o edita una inscripción completa en una sola transacción (función
 * colonia_guardar_inscripcion de la 0020). Devuelve el id de la inscripción.
 */
export async function guardarInscripcion(payload) {
  return run(supabase.rpc("colonia_guardar_inscripcion", { p: payload }));
}

// ---------- Tanda 2: datos para Resumen, Equipo, Ingresos y egresos, Empresa ----------

/** Ítems con temporada, estado y condición del chico (vista colonia_items_detalle). */
export async function fetchItemsDetalle({ seasonId = null } = {}) {
  return fetchAll(() => {
    let q = supabase.from("colonia_items_detalle").select("*");
    if (seasonId) q = q.eq("season_id", seasonId);
    return q.order("id");
  });
}

/** Cobros con su temporada y mes de servicio (vista colonia_cobros_detalle). */
export async function fetchCobrosDetalle({ seasonId = null } = {}) {
  return fetchAll(() => {
    let q = supabase.from("colonia_cobros_detalle").select("*");
    if (seasonId) q = q.eq("season_id", seasonId);
    return q.order("id");
  });
}

export async function fetchStaffCategorias() {
  return run(supabase.from("colonia_staff_categorias").select("*").order("orden"));
}

export async function saveStaffCategoria(row, id = null) {
  if (id) return run(supabase.from("colonia_staff_categorias").update(row).eq("id", id).select().single());
  return run(supabase.from("colonia_staff_categorias").insert(row).select().single());
}

export async function deleteStaffCategoria(id) {
  await run(supabase.from("colonia_staff_categorias").delete().eq("id", id));
}

export async function fetchStaff({ seasonId = null } = {}) {
  let q = supabase.from("colonia_staff").select("*");
  if (seasonId) q = q.eq("season_id", seasonId);
  return run(q.order("created_at"));
}

export async function saveStaff(row, id = null) {
  if (id) return run(supabase.from("colonia_staff").update(row).eq("id", id).select().single());
  return run(supabase.from("colonia_staff").insert(row).select().single());
}

export async function deleteStaff(id) {
  await run(supabase.from("colonia_staff").delete().eq("id", id));
}

export async function fetchGastos({ seasonId = null } = {}) {
  let q = supabase.from("colonia_gastos").select("*");
  if (seasonId) q = q.eq("season_id", seasonId);
  return run(q.order("mes").order("created_at"));
}

export async function saveGasto(row, id = null) {
  if (id) return run(supabase.from("colonia_gastos").update(row).eq("id", id).select().single());
  return run(supabase.from("colonia_gastos").insert(row).select().single());
}

export async function deleteGasto(id) {
  await run(supabase.from("colonia_gastos").delete().eq("id", id));
}

export async function fetchEmpresaCategorias() {
  return run(supabase.from("empresa_categorias").select("*").order("orden"));
}

export async function saveEmpresaCategoria(row, id = null) {
  if (id) return run(supabase.from("empresa_categorias").update(row).eq("id", id).select().single());
  return run(supabase.from("empresa_categorias").insert(row).select().single());
}

export async function deleteEmpresaCategoria(id) {
  await run(supabase.from("empresa_categorias").delete().eq("id", id));
}

export async function fetchEstructura() {
  return run(supabase.from("empresa_estructura").select("*").order("concepto").order("desde"));
}

export async function saveEstructura(row, id = null) {
  if (id) return run(supabase.from("empresa_estructura").update(row).eq("id", id).select().single());
  return run(supabase.from("empresa_estructura").insert(row).select().single());
}

export async function deleteEstructura(id) {
  await run(supabase.from("empresa_estructura").delete().eq("id", id));
}

/** Para el Backup completo (tanda 2). */
export async function fetchTanda2Backup() {
  const [staffCategorias, staff, gastos, empresaCategorias, estructura] = await Promise.all([
    fetchStaffCategorias(),
    fetchStaff(),
    fetchGastos(),
    fetchEmpresaCategorias(),
    fetchEstructura(),
  ]);
  return { staffCategorias, staff, gastos, empresaCategorias, estructura };
}
